import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDeepCompareEffect } from 'react-use'

import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'

import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import {
	Appointment,
	AppointmentProtocolRule,
	ClinicalProcess,
	MedicalActProtocolRule,
	Options,
	Requisition,
} from 'models'

import FinalizeTaskDialog from '../FinalizeTaskController/FinalizeTaskDialog'

import useSubmitTask from 'hooks/useSubmitTask'
import {
	ApproveTransferClinicalSchema,
	ExtendedTransferClinicalApprovalSchema,
	TransferClinicalApprovalSchema,
} from './TransferClinicalApprovalSchema'
import { useNoWaitFetchList } from 'store'
import useSession from 'hooks/useSession'
import HFDropdown from 'components/HFInputs/HFDropdown'
import TaskNotesController from '../../TaskSubControllers/TaskNotesController/TaskNotesController'
import RequestProcess from '../../../../models/RequestProcess'
import AlertBox from 'components/AlertBox/AlertBox'

import styles from '../Dialog.module.sass'
import { TransferClinicalApprovalTaskInfo } from './TransferClinicalApprovalTaskInfo'
import TaskHeaderController from 'controllers/TaskManager/TaskSubControllers/TaskHeader/TaskHeader'
import TaskMenuController from '../TaskMenu/TaskMenuController'
import ExpandableWidget from 'components/ExpandableWidget/ExpandableWidget'
import HFTextInput from 'components/HFInputs/HFTextInput'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import { TaskNeedsController } from 'controllers/TaskManager/TaskSubControllers/TaskNeedsController/TaskNeedsController'
import TaskRefusalController from 'controllers/TaskManager/TaskSubControllers/TaskRefusalController/TaskRefusalController'
import PersonalAccidentWarning from 'controllers/TaskManager/TaskSubControllers/PersonalAccidentWarning'
import HFAppointmentProtocol from 'components/HFInputs/HFAppointmentProtocol'
import HFMedicalActsProtocol from 'components/HFInputs/HFMedicalActsProtocol'
import ScheduledAppointments from 'components/cards/AppointmentCard/ScheduledAppointment/ScheduledAppointments'

import protocolMedicalActs from '../../../../dialogs/DataEditingDialogs/EditProcessProtocolDialog/ProtocolMedicalActs.json'
import { Segment, RequisitionStatus } from 'utils/enums'
import {
	APPOINTMENT_PROTOCOLS,
	MEDICAL_ACT_PROTOCOLS,
} from 'dialogs/DataEditingDialogs/EditProcessProtocolDialog/EditProcessProtocolDialog'
import { useReload } from 'store'
import { API_URL, parseJSONContent } from 'store/fetchUtils'

const TransferClinicalApprovalTaskController = (props) => {
	const { i18n } = useGlobal()
	const { data, resolve, dismiss, id } = props
	const reload = useReload()
	const [defaultValues, setDefaultValues] = useState(null)
	const [argValues, setArgValues] = useState(null)
	const [submissionType, setSubmissionType] = useState(null)
	const history = useHistory()
	const { user: sessionUser } = useSession()
	const postalCode = data?.injured.postalCode
	const accidentType = data?.segment.id
	const suggestionsLogRef = useRef({})
	const validateRef = useRef({})
	const [showWarning, setShowWarning] = useState(false)
	const [blockCount, setBlockCount] = useState(0)

	const isAdministrative = sessionUser?.roles.includes('manager')

	const schema = isAdministrative
		? submissionType === 'approve'
			? ApproveTransferClinicalSchema({ ...argValues })
			: ExtendedTransferClinicalApprovalSchema({ ...argValues })
		: TransferClinicalApprovalSchema({ ...argValues })

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(schema),
		shouldUnregister: true,
		defaultValues: {
			appointmentsProtocol: [],
			medicalActsProtocol: [],
		},
	})
	const { handleSubmit, watch, reset, formState, getValues, setValue } =
		methods

	const {
		error,
		submitting,
		resolveTask,
		approveTask,
		requestApprovalTask,
		requestManagerApprovalTask,
	} = useSubmitTask(data?.id, formState?.errors)

	const [specialtyId] = watch(['specialtyId'])
	const fetchServiceProviders = !!specialtyId

	const { contents } = useNoWaitFetchList(
		[
			`clinical_processes/${data?.processId}`,
			`clinical_processes/${data?.processId}/appointments`,
			`clinical_processes/${data?.processId}/requisitions`,
			`clinical_processes/${data?.processId}/requisitions/${data?.requisitionId}/notes`,
			`clinical_processes/${data?.processId}/request_process`,
			`clinical_processes/${data?.processId}/requisitions/${data?.requisitionId}`,
			`clinical_processes/${data.processId}/requisitions/${data.requisitionId}/specialties`,
			fetchServiceProviders &&
				`clinical_processes/${data.processId}/requisitions/${data.requisitionId}/specialties/${specialtyId}/service_providers`,
			'/metadata/medical_specialties',
			'/metadata/medical_acts',
			`metadata/provider_entities`,
			`/rules/clinical_process/${data?.processId}?ruleType=AutomationRule&taskType=AppointmentClinicalApproval`,
			`/rules/clinical_process/${data?.processId}?ruleType=AutomationRule&taskType=MedicalActsClinicalApproval`,
		],
		[
			ClinicalProcess.fromJSON,
			Appointment.fromJSON,
			Requisition.fromJSON,
			(json) => json.notes,
			RequestProcess.fromJSON,
			Requisition.fromJSON,
			Options.fromSimpleObjectJSON,
			fetchServiceProviders && Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
		]
	)

	const [
		clinicalProcessContents,
		appointmentsContent,
		requisitionsContent,
		notesContents,
		requestProcessContents,
		requisitionContents,
		medicalSpecialtiesContents,
		serviceProvidersContents,
		allMedicalSpecialtiesContents,
		medicalActsContents,
		providerEntitiesContents,
		appointmentProcessProtocolsContents,
		medicalActProcessProtocolsContents,
	] = contents

	const { contents: clinicalProcess } =
		clinicalProcessContents?.state === 'hasValue'
			? clinicalProcessContents
			: { state: clinicalProcessContents?.state }
	const { contents: appointments } =
		appointmentsContent?.state === 'hasValue'
			? appointmentsContent
			: { state: appointmentsContent?.state }
	const { contents: requisitions } =
		requisitionsContent?.state === 'hasValue'
			? requisitionsContent
			: { state: requisitionsContent?.state }
	const { contents: notes, state: notesState } =
		notesContents?.state === 'hasValue'
			? notesContents
			: { state: notesContents?.state }
	const { contents: requisition, state: requisitionState } =
		requisitionContents?.state === 'hasValue'
			? requisitionContents
			: { state: requisitionContents?.state }
	const { contents: medicalSpecialties, state: medicalSpecialtiesState } =
		medicalSpecialtiesContents?.state === 'hasValue'
			? medicalSpecialtiesContents
			: { state: medicalSpecialtiesContents?.state }
	const { contents: serviceProviders, state: serviceProvidersState } =
		serviceProvidersContents?.state === 'hasValue'
			? serviceProvidersContents
			: { state: serviceProvidersContents?.state }
	const { contents: providerEntities } =
		providerEntitiesContents?.state === 'hasValue'
			? providerEntitiesContents
			: { state: providerEntitiesContents?.state }
	const { contents: requestProcess } =
		requestProcessContents?.state === 'hasValue'
			? requestProcessContents
			: { state: requestProcessContents?.state }
	const { contents: allMedicalSpecialties } =
		allMedicalSpecialtiesContents?.state === 'hasValue'
			? allMedicalSpecialtiesContents
			: { state: allMedicalSpecialtiesContents?.state }
	const { contents: medicalActs } =
		medicalActsContents?.state === 'hasValue'
			? medicalActsContents
			: { state: medicalActsContents?.state }
	const { contents: appointmentProcessProtocols } =
		appointmentProcessProtocolsContents?.state === 'hasValue'
			? appointmentProcessProtocolsContents
			: { state: appointmentProcessProtocolsContents?.state }
	const { contents: medicalActProcessProtocols } =
		medicalActProcessProtocolsContents?.state === 'hasValue'
			? medicalActProcessProtocolsContents
			: { state: medicalActProcessProtocolsContents?.state }

	const isLoading = requisitionState === 'loading'
	const isNotesLoading = notesState === 'loading'
	const isServiceProvidersLoading = [
		medicalSpecialtiesState,
		serviceProvidersState,
	].includes('loading')
	const availableProtocolMedicalActs = new Set(protocolMedicalActs)
	const availableProtocolMedicalActsOptions = medicalActs?.filter(
		({ text: ma }) => availableProtocolMedicalActs.has(ma.split(' ')?.[0])
	)
	const [includedAppointments, setIncludedAppointments] = useState({})
	const [hasFilteredAppointments, setHasFilteredAppointments] =
		useState(false)
	const [noTransferAppointments, setNoTransferAppointments] = useState(false)

	const scheduledAppointments = appointments ?? []
	const [customError, setCustomError] = useState([])
	const errorTopRef = useRef(null)

	const [linkedTransferAppointmentIds, setLinkedTransferAppointmentIds] =
		useState([])

	useEffect(() => {
		const processId = data?.processId
		const requisitionId = data?.requisitionId
		if (!processId || !requisitionId) return

		const token =
			localStorage.getItem('session-token') ||
			sessionStorage.getItem('session-token')
		if (!token) return

		fetch(
			`${API_URL}/api/clinical_processes/${processId}/requisitions/${requisitionId}/transfer-links`,
			{ method: 'GET', headers: { Authorization: `Bearer ${token}` } }
		)
			.then((r) => parseJSONContent(r))
			.then((res) => {
				setLinkedTransferAppointmentIds(res?.appointmentIds ?? [])
			})
			.catch(() => {
				setLinkedTransferAppointmentIds([])
			})
	}, [data?.processId, data?.requisitionId])

	useEffect(() => {
		if (serviceProvidersState !== 'loading' && serviceProviders?.length) {
			const providerId = requisition?.establishment?.id.toString()
			const hasDefaultProvider =
				serviceProviders.findIndex(
					({ value }) => value.toString() === providerId
				) >= 0
			setValue('providerId', hasDefaultProvider ? providerId : '')
		}
	}, [
		requisition?.establishment?.id,
		serviceProvidersState,
		serviceProviders?.length,
	])

	useEffect(() => {
		if (
			!isLoading &&
			(data.formState || defaultValues) &&
			!formState.isDirty &&
			formState.submitCount === 0
		) {
			reset && reset(data.formState ? data.formState : defaultValues)
		}
	}, [
		reset,
		defaultValues,
		isLoading,
		data.formState,
		formState.isDirty,
		formState.submitCount,
	])

	useDeepCompareEffect(() => {
		if (!isLoading) {
			let defaultValues = {}

			if (requisition) {
				setArgValues({
					requestEscort: requisition?.requestEscort,
					establishmentId: requisition?.establishment?.id,
					requisitionStateId: requisition?.status?.id,
				})
			}

			if (requisition?.requestEscort && isAdministrative) {
				defaultValues = {
					...defaultValues,
					'needsOptions.housing': false,
					'needsOptions.transport': false,
				}
			}

			if (requisition?.specialty) {
				defaultValues = {
					...defaultValues,
					specialtyId: `${requisition?.specialty.id}`,
				}
			}

			if (isAdministrative) {
				defaultValues = {
					...defaultValues,
					notes: requisition?.notes || '',
					administrativeObservation:
						requisition?.administrativeObservation || '',
				}
			}

			if (!isAdministrative && requisition?.requestEscort) {
				defaultValues = { ...defaultValues, needEscort: false }
			}

			defaultValues = {
				...defaultValues,
				'controlAppointmentRequest.controlAppointment': false,
				changeProvider: false,
			}

			if (serviceProviders) {
				const establishmentId =
					requisition?.establishment?.id.toString()
				const hasEstablishment = serviceProviders.some(
					(provider) => provider.value === establishmentId
				)
				if (hasEstablishment) {
					defaultValues = {
						providerId: establishmentId,
						...defaultValues,
					}
				}
			}

			if (isAdministrative && appointmentProcessProtocols?.length) {
				defaultValues = {
					...defaultValues,
					appointmentsProtocol:
						AppointmentProtocolRule.retrieveRuleValues(
							appointmentProcessProtocols
						),
				}
			}

			if (isAdministrative && medicalActProcessProtocols?.length) {
				defaultValues = {
					...defaultValues,
					medicalActsProtocol:
						MedicalActProtocolRule.retrieveRuleValues(
							medicalActProcessProtocols
						),
				}
			}

			setDefaultValues({ ...defaultValues })

			if (
				isAdministrative &&
				clinicalProcess?.accident?.segment?.abbreviation ===
					Segment.WorkRelated &&
				(APPOINTMENT_PROTOCOLS || MEDICAL_ACT_PROTOCOLS)
			) {
				setTimeout(() => {
					setValue(
						'appointmentsProtocol',
						defaultValues.appointmentsProtocol ?? [],
						{ shouldDirty: true, shouldTouch: true }
					)
					setValue(
						'medicalActsProtocol',
						defaultValues.medicalActsProtocol ?? [],
						{ shouldDirty: true, shouldTouch: true }
					)
				}, 100)
			}
		}
	}, [isLoading, requisition ?? {}, serviceProviders ?? []])

	// ── Submit ─────────────────────────────────────────────────────────────────

	const onSubmit = async (formData) => {
		const hasBlocked = Object.values(validateRef.current).some(Boolean)
		if (hasBlocked) {
			setShowWarning(true)
			return
		}

		let finalData = {}

		const {
			changeProvider,
			controlAppointmentRequest,
			appointmentsProtocol,
			medicalActsProtocol,
			...remainingData
		} = formData

		finalData = { ...remainingData }

		const filteredAppointments = appointmentsProtocol?.filter(
			({ specialtyId }) => specialtyId !== ''
		)
		const appointmentsRequestBody =
			AppointmentProtocolRule.constructRequestBody(
				filteredAppointments,
				`Automação protocolo para processo: ${data?.processId}`
			)

		const filteredMedicalActs = medicalActsProtocol?.filter(
			({ medicalActId }) => medicalActId !== ''
		)
		const medicalActsRequestBody =
			MedicalActProtocolRule.constructRequestBody(
				filteredMedicalActs,
				`Automação protocolo para processo: ${data?.processId}`
			)

		if (isAdministrative) {
			finalData = {
				...finalData,
				appointmentsProtocol: appointmentsRequestBody,
				medicalActsProtocol: medicalActsRequestBody,
			}
		}

		if (
			controlAppointmentRequest?.serviceProviderId &&
			controlAppointmentRequest?.doctorId
		) {
			finalData = {
				...finalData,
				controlAppointmentRequest: {
					serviceProviderId:
						controlAppointmentRequest?.serviceProviderId,
					doctorId: controlAppointmentRequest?.doctorId,
				},
			}
		}

		if (formData.specialtyId ?? requisition.specialty?.id) {
			finalData = {
				...finalData,
				specialtyId: formData.specialtyId ?? requisition.specialty?.id,
			}
		}

		const transferList = Object.keys(includedAppointments).filter(
			(id) => includedAppointments[id]
		)

		if (
			transferList.length === 0 &&
			!noTransferAppointments &&
			props?.data?.requisitionType?.id === 13 &&
			hasFilteredAppointments
		) {
			errorTopRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			})
			setCustomError((prev) => [
				...prev,
				{
					title: i18n`Error`,
					text: 'Necessário selecionar pelo menos uma consulta ou assinalar "Não transferir nenhuma especialidade"',
				},
			])
			return
		}
		finalData = {
			...finalData,
			transferAppointments: transferList,
		}
		const saveLogs = async () => {
			try {
				const logEntries = filteredMedicalActs
					.map((act) => {
						const suggestion =
							suggestionsLogRef.current[String(act.medicalActId)]
						if (!suggestion?.response?.providerReferrals?.length)
							return null
						return {
							...suggestion,
							providerId: act.serviceProviderId || null,
							userJustification: act.userJustification ?? null,
							userJustificationId:
								act.userJustificationId ?? null,
						}
					})
					.filter(Boolean)

				for (const act of logEntries) {
					try {
						const logRequestBodyWithoutRequest = {
							processId: data?.processId,
							requestingProviderId: null,
							requisitionId: null,
							codes: String(act.actId),
							postalCode: postalCode,
							scheduleDate: null,
							appointmentDate: null,
							providers:
								act.response?.providerReferrals?.map((p) => ({
									providerId: p.providerId,
									profitProvider: Math.round(p.profit || 0),
									distance: p.distance || 0,
									speed: p.averageTime || 0,
								})) || [],
							chosenProvider: act.providerId || null,
							username:
								JSON.parse(localStorage.getItem('user'))
									?.username ?? null,
							justification:
								act.userJustification ||
								act.response?.description ||
								'Aprovação manual',
							userJustification: act.userJustification ?? null,
							userJustificationId:
								act.userJustificationId ?? null,
							suggestType: act.suggestType ?? null,
						}

						await fetch(`${API_URL}/api/suggest/logs`, {
							method: 'POST',
							headers: { 'Content-Type': 'application/json' },
							body: JSON.stringify({
								...logRequestBodyWithoutRequest,
								request: JSON.stringify(
									logRequestBodyWithoutRequest
								),
							}),
						})
					} catch (error) {
						console.error(
							`Erro ao guardar log para ${act.actId}:`,
							error
						)
					}
				}
			} catch (error) {
				console.error('Erro ao gravar logs:', error)
			}
		}

		if (submissionType === 'resolve') {
			resolveTask(finalData)
				.then(async () => {
					reload([
						`/rules/clinical_process/${data?.processId}?ruleType=AutomationRule&taskType=AppointmentClinicalApproval`,
						`/rules/clinical_process/${data?.processId}?ruleType=AutomationRule&taskType=MedicalActsClinicalApproval`,
					])
					await resolve()
					await saveLogs()
				})
				.catch((error) =>
					console.error('Erro no resolve - Não gravou logs:', error)
				)
		} else if (submissionType === 'approve') {
			approveTask(finalData)
				.then(async () => {
					await resolve()
					await saveLogs()
				})
				.catch((error) =>
					console.error('Erro no approve - Não gravou logs:', error)
				)
		}
	}

	useEffect(() => {
		if (error) {
			setCustomError((prev) => [
				...prev,
				...error.map((err) => ({ title: i18n`Error`, text: err })),
			])
		}
	}, [error])
	const displayLoadingIndicator = isLoading && !formState.isDirty
	const isFromIsland = data?.injured?.isFromIsland

	return (
		<>
			{displayLoadingIndicator && <LoadingIndicator />}
			{!displayLoadingIndicator && (
				<>
					{submitting && (
						<FinalizeTaskDialog
							dismiss={dismiss}
							submitting={submitting}
							task={data}
						/>
					)}

					<FormProvider {...methods}>
						<div className={styles['form-container']}>
							<TaskHeaderController
								task={data}
								requisition={requisition}
								hideSpecialtyAndAppointment
							/>

							<div className={styles['notes-form-container']}>
								<TaskNotesController
									data={{
										id: data.id,
										processId: data.processId,
										taskNote: notes,
									}}
									requisition={requisition}
									isNotesLoading={isNotesLoading}
								/>
							</div>

							<form
								id={`form_${id}`}
								className={styles['form']}
								onSubmit={handleSubmit(onSubmit)}
							>
								{customError && customError.length > 0 && (
									<div ref={errorTopRef}>
										<AlertBox
											className={styles['alert-box']}
											type='error'
											items={customError}
										/>
									</div>
								)}

								<TaskRefusalController
									requisition={requisition}
								/>

								<PersonalAccidentWarning task={data} />

								<TransferClinicalApprovalTaskInfo
									isAdministrative={isAdministrative}
									requisition={requisition}
									requestProcess={requestProcess}
									data={data}
								/>

								<fieldset>
									<VStack gap='xsmall'>
										<div>
											<h5>{i18n`Medical specialty`}</h5>
											<HFDropdown
												name='specialtyId'
												options={
													medicalSpecialties ?? []
												}
												optionsAlign='right'
												displayValue={true}
												searchable={true}
												noData={i18n`No results`}
											/>
										</div>

										{requisition?.status?.id !==
											RequisitionStatus.WaitingApproval && (
											<>
												<div>
													<h5>{i18n`Service provider`}</h5>
													<HFDropdown
														name='providerId'
														options={
															serviceProviders ??
															[]
														}
														optionsAlign='right'
														displayValue={true}
														searchable={true}
														noData={i18n`No results`}
													/>
												</div>
												{isServiceProvidersLoading && (
													<LoadingIndicator
														className={
															styles[
																'inner-loading-indicator'
															]
														}
													/>
												)}
											</>
										)}
									</VStack>
								</fieldset>

								{props?.data?.requisitionType?.id === 13 && (
									<fieldset>
										<VStack gap='xsmall'>
											<ScheduledAppointments
												appointments={
													scheduledAppointments
												}
												onSelectionChange={
													setIncludedAppointments
												}
												onHasFiltered={
													setHasFilteredAppointments
												}
												onNoTransferChange={
													setNoTransferAppointments
												}
												appointmentId={
													requisition?.requestingAppointmentId
												}
												linkedAppointmentIds={
													linkedTransferAppointmentIds
												}
											/>
										</VStack>
									</fieldset>
								)}

								{isFromIsland && (
									<TaskNeedsController
										isAdministrative={isAdministrative}
										requisition={requisition}
									/>
								)}

								{isAdministrative &&
									clinicalProcess?.accident?.segment
										?.abbreviation ===
										Segment.WorkRelated &&
									(APPOINTMENT_PROTOCOLS ||
										MEDICAL_ACT_PROTOCOLS) && (
										<>
											<h4>{i18n`Appointments protocols`}</h4>
											<fieldset>
												<HFAppointmentProtocol
													data={{
														medicalSpecialties:
															allMedicalSpecialties,
														appointments,
														processId:
															data?.processId,
													}}
													name={
														'appointmentsProtocol'
													}
												/>
											</fieldset>

											<h4>{i18n`Medical acts protocols`}</h4>
											<fieldset>
												{clinicalProcess && (
													<HFMedicalActsProtocol
														data={{
															medicalActs:
																availableProtocolMedicalActsOptions,
															requisitions:
																requisitions ||
																[],
															processId:
																data?.processId,
															processPostalCode:
																postalCode,
															accidentType:
																accidentType,
															onUpdateFormData:
																() => {},
															requestingProviderId:
																requisition
																	?.requestingProvider
																	?.id,
															insuranceId:
																clinicalProcess
																	?.insurancePolicy
																	?.insuranceCompany
																	?.id,
															requisitionStateId:
																requisition
																	?.status
																	?.id,
														}}
														injuredBirthday={
															data?.injured
																?.birthdayInfo
																?.birthdayDate
																? data.injured.birthdayInfo.birthdayDate
																		.split(
																			'/'
																		)
																		.reverse()
																		.join(
																			'-'
																		)
																: null
														}
														injuredType={null}
														bodyPart={null}
														name={
															'medicalActsProtocol'
														}
														onSuggestionsChange={(
															log
														) => {
															suggestionsLogRef.current =
																log
														}}
														blockCountDispatcher={(
															delta
														) =>
															setBlockCount(
																(c) => c + delta
															)
														}
														validateRef={
															validateRef
														}
														showWarning={
															showWarning
														}
													/>
												)}
											</fieldset>
										</>
									)}

								<fieldset
									className={styles['observation-container']}
								>
									{isAdministrative ? (
										<ExpandableWidget
											className={styles['observation']}
											heading='h5'
											title={i18n`Administrative Observations`}
										>
											<HFTextInput
												name='administrativeObservation'
												placeholder={i18n`Administrative observation (optional)`}
												multiline={true}
												rows={5}
												noResize
											/>
										</ExpandableWidget>
									) : (
										<ExpandableWidget
											className={styles['observation']}
											heading='h5'
											title={i18n`Clinical Observations`}
										>
											<HFTextInput
												name='medicalObservation'
												placeholder={i18n`Clinical observation (optional)`}
												multiline={true}
												rows={5}
												noResize
											/>
										</ExpandableWidget>
									)}
								</fieldset>
							</form>
						</div>
					</FormProvider>
					{isAdministrative ? (
						<TaskMenuController
							data={{
								formId: id,
								options: ['postpone', 'forward', 'refuse'],
								submitting,
								setSubmissionType,
								submissionType: 'resolve',
								requestApprovalTask,
								requestManagerApprovalTask,
								getValues,
								appointments: scheduledAppointments,
								appointmentId:
									requisition?.requestingAppointmentId,
								disableResolver: false,
							}}
						/>
					) : (
						<TaskMenuController
							data={{
								formId: id,
								options: ['postpone', 'forward', 'refuse'],
								history,
								submitting,
								setSubmissionType,
								submissionType: 'approve',
								getValues,
								appointments: scheduledAppointments,
								appointmentId:
									requisition?.requestingAppointmentId,
								disableResolver: false,
							}}
						/>
					)}
				</>
			)}
		</>
	)
}

export default TransferClinicalApprovalTaskController
